import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { usePageRevisionFamilyEdits } from 'features/knowledgeDocumentViewer/hooks/usePageRevisionFamilyEdits'
import { usePageRevisionFamilyPages } from 'features/knowledgeDocumentViewer/hooks/usePageRevisionFamilyPages'
import type { DocumentPageMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { type SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { systemDocumentPagesByPageIds } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { mergePageMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { effectivePageRevisionFamilyKey } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import {
  editedPageIdsForDrawingNumber,
  latestRevisionFamilyEditsByPageId,
  pageMatchesRevisionFamilyScope,
  resolvePageRevisionFamilyCandidates,
  revisionFamilyEditForPage,
  revisionFamilyPageCandidatesByDrawingNumber,
} from 'features/knowledgeDocumentViewer/utils/pageRevisionFamilyDiscovery'
import { useMemo } from 'react'

const EMPTY_PAGES: SystemDocumentPageEntry[] = []
const EMPTY_EDITS: DocumentPageMetadataUserEditEntry[] = []

export function usePageRevisionFamilyDiscovery({
  pageEntry,
  pageUserEdit,
  enabled,
}: {
  pageEntry: SystemDocumentPageEntry | undefined
  pageUserEdit: DocumentPageMetadataUserEditEntry | undefined
  enabled: boolean
}) {
  const { siteID } = useKnowledgeDocumentDataContext()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const scopedTags = useMemo(() => {
    if (!enabled || !pageEntry || isPipelineConfigScopeLoading) return undefined
    const tags = getPipelineConfigScopedTags({ site: siteID })
    return tags && pageMatchesRevisionFamilyScope(pageEntry, tags)
      ? tags
      : undefined
  }, [
    enabled,
    pageEntry,
    isPipelineConfigScopeLoading,
    getPipelineConfigScopedTags,
    siteID,
  ])
  const {
    data: edits,
    isLoading: isLoadingEdits,
    mutate: mutatePriorEdits,
  } = usePageRevisionFamilyEdits(siteID, !!scopedTags)
  const editsByPageId = useMemo(
    () => latestRevisionFamilyEditsByPageId(edits ?? EMPTY_EDITS, pageUserEdit),
    [edits, pageUserEdit]
  )
  const currentEditValues = pageEntry
    ? revisionFamilyEditForPage(pageEntry, editsByPageId)?.entry.data.values
    : undefined
  const drawingNumber = mergePageMetadataValues(
    pageEntry?.entry.data,
    currentEditValues
  ).issuer_drawing_number
  const currentFamilyKey = effectivePageRevisionFamilyKey(
    pageEntry,
    currentEditValues ?? undefined
  )
  const generatedLoadOptions = useMemo(
    () =>
      scopedTags && drawingNumber?.trim()
        ? revisionFamilyPageCandidatesByDrawingNumber(drawingNumber, scopedTags)
        : undefined,
    [drawingNumber, scopedTags]
  )
  const { data: rawGeneratedPages, isLoading: isLoadingGeneratedPages } =
    usePageRevisionFamilyPages(generatedLoadOptions)
  const editedPageIds = useMemo(
    () => editedPageIdsForDrawingNumber(editsByPageId, drawingNumber),
    [editsByPageId, drawingNumber]
  )
  const editedLoadOptions = useMemo(
    () =>
      scopedTags
        ? systemDocumentPagesByPageIds(editedPageIds, scopedTags)
        : undefined,
    [editedPageIds, scopedTags]
  )
  const { data: rawEditedPages, isLoading: isLoadingEditedPages } =
    usePageRevisionFamilyPages(editedLoadOptions)
  const isDiscoveryReady =
    !scopedTags ||
    (edits !== undefined &&
      (!generatedLoadOptions || rawGeneratedPages !== undefined) &&
      (!editedLoadOptions || rawEditedPages !== undefined))
  const pages = useMemo(() => {
    if (!scopedTags || !isDiscoveryReady) return EMPTY_PAGES
    return resolvePageRevisionFamilyCandidates({
      pages: [
        ...(rawGeneratedPages ?? EMPTY_PAGES),
        ...(rawEditedPages ?? EMPTY_PAGES),
        ...(pageEntry ? [pageEntry] : []),
      ],
      currentFamilyKey,
      editsByPageId,
      scopedTags,
    })
  }, [
    scopedTags,
    isDiscoveryReady,
    rawGeneratedPages,
    rawEditedPages,
    pageEntry,
    currentFamilyKey,
    editsByPageId,
  ])
  return {
    pages,
    editsByPageId,
    currentFamilyKey,
    mutatePriorEdits,
    areEditsReady: isDiscoveryReady,
    isLoading:
      enabled &&
      (isPipelineConfigScopeLoading ||
        isLoadingEdits ||
        isLoadingGeneratedPages ||
        isLoadingEditedPages),
  }
}
